import { orderBy } from 'lodash';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { Affiliate } from './config';
import { environment } from '@environment';
import { DOCUMENT } from '@angular/common';
import * as fromCore from '@metutors/core/state';
import { ICountry, ISubscription, IUser } from './core/models';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Inject, OnInit, Component, OnDestroy, ViewChild } from '@angular/core';
import {
  map,
  filter,
  mergeMap,
  Observable,
  Subscription,
  withLatestFrom,
} from 'rxjs';
import {
  Router,
  RouterEvent,
  NavigationEnd,
  ActivatedRoute,
  NavigationStart,
  NavigationError,
  NavigationCancel,
} from '@angular/router';

@Component({
  selector: 'metutors-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('progressBar', { static: true }) progressBar: any;

  ipApi$: Observable<any>;
  returnUrl$: Observable<string>;
  user$: Observable<IUser | null>;
  paddingTop$: Observable<number>;
  signUpTitle$: Observable<string>;
  currencyRates$: Observable<any[]>;
  selectedCurrency$: Observable<any>;
  openSignInModal$: Observable<boolean>;
  signInSubHeading$: Observable<string>;
  openSignUpModal$: Observable<boolean>;
  loadingCountries$: Observable<boolean>;
  countries$: Observable<ICountry[] | null>;
  subscription$: Observable<ISubscription | null>;
  openLanguageCurrencyModal$: Observable<boolean>;

  url: string;
  layout?: any;
  selectedLanguage: string;
  routerSubscription$: Subscription;
  maintenanceMode = environment.maintenanceMode;

  constructor(
    private _router: Router,
    private _store: Store<any>,
    private _route: ActivatedRoute,
    public translate: TranslateService,
    @Inject(DOCUMENT) private _document: Document
  ) {
    if (typeof window !== 'undefined') {
      translate.addLangs(['ar', 'en']);
      translate.setDefaultLang('ar');

      const browserLang = translate.getBrowserLang();
      const defaultLanguage = localStorage.getItem('DEFAULT_LANGUAGE');

      if (!defaultLanguage)
        localStorage.setItem(
          'DEFAULT_LANGUAGE',
          browserLang?.match(/en|ar/)
            ? browserLang
            : translate.currentLang || 'ar'
        );

      translate.use(
        defaultLanguage?.match(/en|ar/)
          ? defaultLanguage
          : browserLang?.match(/en|ar/)
          ? browserLang
          : translate.currentLang || 'ar'
      );
      this._checkLangauge();

      translate.onLangChange.subscribe(() => this._checkLangauge());
    }

    this.routerSubscription$ = this._router.events.subscribe((event: any) => {
      this.url = this._router.url?.split('?')[0]?.split('#')[0];
      this._navigationInterceptor(event);
    });
  }

  ngOnInit(): void {
    this._prepareCountries();
    this._store.dispatch(fromCore.getIpApi());
    this._store.dispatch(fromCore.identifyUser());
    this._store.dispatch(fromCore.loadCurrencyRates());
    this._store.dispatch(fromCore.loadCurrenciesNames());
    this.user$ = this._store.select(fromCore.selectUser);
    this.ipApi$ = this._store.select(fromCore.selectIpApi);
    this.returnUrl$ = this._store.select(fromCore.selectReturnUrl);
    this.signUpTitle$ = this._store.select(fromCore.selectSignUpTitle);
    this.subscription$ = this._store.select(fromCore.selectSubscription);
    this.paddingTop$ = this._store.select(fromCore.selectWebsitePaddingTop);
    this.openSignInModal$ = this._store.select(
      fromCore.selectIsOpenSignInModal
    );
    this.signInSubHeading$ = this._store.select(
      fromCore.selectSignInSubHeading
    );
    this.openSignUpModal$ = this._store.select(
      fromCore.selectIsOpenSignUpModal
    );
    this.openLanguageCurrencyModal$ = this._store.select(
      fromCore.selectIsOpenLanguageCurrencyModal
    );
    this.selectedLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.selectedLanguage = event.lang;
    });

    this._router.events
      .pipe(
        filter(events => events instanceof NavigationEnd),
        map(evt => this._route),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data)
      )
      .subscribe((x: any) => {
        this.layout = x?.layout;

        if (this.layout?.title && this.layout?.title !== 'MEtutors')
          this.translate.get(this.layout.title).subscribe(title =>
            this.translate.get('METUTORS_PLATFORM').subscribe(metutors => {
              if (this.layout?.description)
                this.translate
                  .get(this.layout.description)
                  .subscribe(description =>
                    this._store.dispatch(
                      fromCore.setMetaTags({
                        tags: {
                          title: this.layout?.title?.includes('_META_TITLE')
                            ? title
                            : `${title} - ${metutors}`,
                          description,
                          // keywords: '',
                          // url: ''
                        },
                      })
                    )
                  );
              else
                this._store.dispatch(
                  fromCore.setMetaTags({
                    tags: {
                      title: `${title} - ${metutors}`,
                      // description: '',
                      // keywords: '',
                      // url: ''
                    },
                  })
                );
            })
          );
        else
          this.translate
            .get('PERSONALIZED_ONLINE_TUTORING_META_TITLE')
            .subscribe(title =>
              this.translate
                .get('PERSONALIZED_ONLINE_TUTORING_META_DESC')
                .subscribe(description =>
                  this._store.dispatch(
                    fromCore.setMetaTags({
                      tags: {
                        title,
                        description,
                        // keywords: '',
                        // url: ''
                      },
                    })
                  )
                )
            );
      });

    this.selectedCurrency$ = this._store
      .select(fromCore.selectCurrentCurrency)
      .pipe(
        withLatestFrom(this._store.select(fromCore.selectCurrenciesNames)),
        map(([currencySymbol, currencies]) => ({
          id: currencySymbol,
          name: `${currencies[currencySymbol]} (${currencySymbol})`,
        }))
      );

    this.currencyRates$ = this._store.select(fromCore.selectCurrencyRates).pipe(
      withLatestFrom(this._store.select(fromCore.selectCurrenciesNames)),
      map(([rates, currencies]) =>
        rates
          ? orderBy(
              Object.keys(rates).map(key => ({
                id: key,
                currency: currencies[key],
                name: `${currencies[key]} (${key})`,
              })),
              ['name', 'asc']
            )
          : []
      )
    );
  }

  onCalculateHeight(padding: number): void {
    this._store.dispatch(
      fromCore.setWebsitePaddingTop({
        paddingTop:
          this.url.includes('/') &&
          this.url !== '/full' &&
          window.innerWidth > 1200
            ? 0
            : padding,
      })
    );
  }

  onLanguageSelect(language: string): void {
    localStorage.setItem('DEFAULT_LANGUAGE', language);
    this.translate.use(language);
    this._store.dispatch(fromCore.changeLanguage({ language }));
  }

  onCurrencySelect(currency: any): void {
    this._store.dispatch(fromCore.changeCurrency({ currency: currency.id }));
  }

  onCloseLanguageCurrencyModal(): void {
    this._store.dispatch(fromCore.closeLanguageCurrencyModal());
  }

  openSignInModal(returnUrl: string): void {
    this._store.dispatch(
      fromCore.openSignInModal({
        uid: '',
        returnUrl,
        affiliate: '',
        subHeading: 'GPT_MODAL',
      })
    );
    this._store.dispatch(fromCore.closeSignUpModal());
  }

  openSignUpModal({
    role,
    returnUrl,
  }: {
    role: number;
    returnUrl: string;
  }): void {
    this._store.dispatch(
      fromCore.openSignUpModal({
        role,
        returnUrl,
        title: 'CREATE_ACCOUNT',
        affiliate: Affiliate.gpt,
      })
    );
    this._store.dispatch(fromCore.closeSignInModal());
  }

  closeSignInModal(): void {
    this._store.dispatch(fromCore.closeSignInModal());
  }

  closeSignUpModal(): void {
    this._store.dispatch(fromCore.closeSignUpModal());
  }

  ngOnDestroy(): void {
    this.routerSubscription$?.unsubscribe();
  }

  private _navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.progressBar.start();
    }

    if (event instanceof NavigationEnd) {
      this.progressBar.complete();

      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
        this._document.body.scrollTop = 0;
        this._document.documentElement.scrollTop = 0;
      }
    }

    if (event instanceof NavigationCancel) {
      this.progressBar.complete();
    }

    if (event instanceof NavigationError) {
      this.progressBar.setConfig({ color: 'red' });
      this.progressBar.complete();
    }
  }

  private _checkLangauge(): void {
    const body = document.getElementsByTagName('body')[0];

    if (this.translate.currentLang === 'ar') {
      moment.locale('ar');
      body.setAttribute('dir', 'rtl');
    } else {
      moment.locale('en');
      body.setAttribute('dir', 'ltr');
    }
  }

  private _prepareCountries(): void {
    this._store.dispatch(fromCore.loadCountries());
    this._store.dispatch(fromCore.loadProgramCountries());
    this.countries$ = this._store.select(fromCore.selectProgramCountries);
    this.loadingCountries$ = this._store.select(
      fromCore.selectIsLoadingProgramCountries
    );
  }
}
